/**
 * PildoresIdPildores.tsx
 * Shows the list of pildores (caza) inside a given Conjunt de Pildores.
 * Reuses all the logic of the original Caza.tsx but fetches only the pildores
 * that belong to the conjunt identified by :id_conjunt in the URL.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Typography, Space, Tooltip, Button, ModalFuncProps,
    Modal, FloatButton, Select, message
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MenuOutlined } from '@ant-design/icons';
import { BiArrowFromLeft, BiEdit } from 'react-icons/bi';
import { SyncOutlined, PlusOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import CazaModal from '../../problemes/content/CazaModal';
import EditAttemptsModal from '../../problemes/content/EditAttemptsModal';
import NouCazaModal from '../../problemes/content/NouCazaModal';
import CazaAdminModal from '../../problemes/content/CazaAdminModal';
import PildoraAlumneDetailModal from './PildoraAlumneDetailModal';
import ImportarCazaExcelModal from '../../problemes/content/ImportarCazaExcelModal';
import { FaEye } from 'react-icons/fa';
import { AuthContext } from '../../../../config/AuthContext';
import UseWebSocketSubscription from '../../../UseWebSocketSubscription';
import { PostCazaStart } from '../../../../dao/jutge/PostCazaStart';
import { ClaimCazaReward } from '../../../../dao/jutge/ClaimCazaReward';
import { CheckCircleOutlined, GiftOutlined, CloseCircleOutlined, FileExcelOutlined, TrophyOutlined } from '@ant-design/icons';
import { EditOutlined } from '@mui/icons-material';
import JavaMon from '../../problemesId/content/JavaMon';
import { EnsureCazaJavamon } from '../../../../dao/jutge/EnsureCazaJavamon';
import { getJavamonImage as getExternalJavamonImage } from '../../../../dao/jutge/GetJavamonConfig';
import RecompensaSummaryModal from '../../activitatsId/content/RecompensaSummaryModal';
import { GetConjuntPildoresCaza } from '../../../../dao/jutge/GetConjuntPildoresCaza';
import { GetPractica } from '../../../../dao/jutge/GetPracticaId';
import PildoraRecompensesModal from './PildoraRecompensesModal';
import { CazaRecompensesTags } from './CazaRecompensesTags';
import { GetCazaRecompenses } from '../../../../dao/jutge/CazaRecompensesDao';

const { Text } = Typography;

interface EscenarioCaza {
    id: number;
    nombre: string;
    descripcion: string;
    dificultad: 'Beginner' | 'Intermediate' | 'Expert';
    imagen: string;
    background?: string;
    nivel?: number;
    rarity?: 'normal' | 'epic' | 'legendary';
    instrucciones: string;
    vidaJavamon: number;
    mustInclude: string[];
    mustNotInclude: string[];
    intentos_restantes?: number;
    intentos_global?: number;
    estat?: number;
    obtained?: number;
    estat_config?: number;
    javamon_config_id?: number;
    url_teoria?: string;
    totalPasos?: number;
    pasos?: any[];
    id_aula?: number;
    aula?: string;
    max_paso_completado?: number;
    rewardType?: string;
    reward_type?: string;
    itemId?: number;
    reward_valor?: number;
    reward_limit_quantity?: number;
    reward_intentos_min?: number;
    reward_intentos_max?: number;
    reward_time_limit?: number;
    tiempo_empleado_segundos?: number;
    tiempo_limite_superado?: number;
    id_origen?: number;
    linked_aules_count?: number;
    ordre?: number;
    id_bloqueo?: number;
    bloqueo_nombre?: string;
    bloqueo_intentado?: number;
}

const dificultadColor: Record<string, string> = {
    Beginner: 'green',
    Intermediate: 'orange',
    Expert: 'red',
};

const CazaImagen: React.FC<{ id: number }> = ({ id }) => {
    const [src, setSrc] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        fetch(`${process.env.REACT_APP_API_URL}:8061/jutge/caza/${id}/imagen`)
            .then(res => { if (res.ok) return res.json(); return null; })
            .then(data => { if (mounted && data && data.background) setSrc(data.background); })
            .catch(() => { });
        return () => { mounted = false; };
    }, [id]);

    return (
        <img
            src={src || ''}
            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}
        />
    );
};

const DragRow = (props: any) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props['data-row-key'],
    });
    const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
        transition,
        cursor: 'move',
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };
    return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};

const PildoresIdPildores: React.FC = () => {
    const { id_conjunt } = useParams<{ id_conjunt: string }>();
    const { userId, user, setBreadCrumb } = useContext(AuthContext);
    const isAdmin = user && (user.tipus === '0' || user.tipus === '1');
    const navigate = useNavigate();

    const [escenarios, setEscenarios] = useState<EscenarioCaza[]>([]);
    const [escenarioActivo, setEscenarioActivo] = useState<EscenarioCaza | null>(null);
    const [loading, setLoading] = useState(false);
    const [editAttemptsVisible, setEditAttemptsVisible] = useState(false);
    const [newCazaVisible, setNewCazaVisible] = useState(false);
    const [editingCaza, setEditingCaza] = useState<EscenarioCaza | null>(null);
    const [selectedCaza, setSelectedCaza] = useState<EscenarioCaza | null>(null);
    const [adminModalVisible, setAdminModalVisible] = useState(false);
    const [activeAdminCaza, setActiveAdminCaza] = useState<EscenarioCaza | null>(null);
    const [reviewCaza, setReviewCaza] = useState<EscenarioCaza | null>(null);
    const [importExcelVisible, setImportExcelVisible] = useState(false);
    const [showBattle, setShowBattle] = useState(false);
    const [battleJavamon, setBattleJavamon] = useState<{ name: string, image: string, rarity: 'normal' | 'epic' | 'legendary' } | null>(null);
    const [activeBattleCazaId, setActiveBattleCazaId] = useState<number | null>(null);
    const [recompensaModalVisible, setRecompensaModalVisible] = useState(false);
    const [recompensaItems, setRecompensaItems] = useState<any[]>([]);
    const [selectableMode, setSelectableMode] = useState(false);
    const [selectionOptions, setSelectionOptions] = useState<any[]>([]);
    const [pendingSelectionItem, setPendingSelectionItem] = useState<EscenarioCaza | null>(null);
    const [pendingJavamonBattle, setPendingJavamonBattle] = useState<{ idCaza: number, javamon: any } | null>(null);
    const [cazaRecompensesVisible, setCazaRecompensesVisible] = useState(false);
    const [selectedCazaRecompenses, setSelectedCazaRecompenses] = useState<EscenarioCaza | null>(null);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [cazaBloqueoModalVisible, setCazaBloqueoModalVisible] = useState(false);
    const [selectedCazaBloqueo, setSelectedCazaBloqueo] = useState<EscenarioCaza | null>(null);
    const [selectedCazaBloqueoId, setSelectedCazaBloqueoId] = useState<number | undefined>(undefined);
    const [savingCazaBloqueo, setSavingCazaBloqueo] = useState(false);

    const handleOpenBloqueoModal = (caza: EscenarioCaza, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedCazaBloqueo(caza);
        setSelectedCazaBloqueoId(caza.id_bloqueo || undefined);
        setCazaBloqueoModalVisible(true);
    };

    const handleSaveCazaBloqueo = async () => {
        if (!selectedCazaBloqueo) return;
        setSavingCazaBloqueo(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}:8061/jutge/caza/${selectedCazaBloqueo.id}/bloqueo`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_bloqueo: selectedCazaBloqueoId || null })
            });
            if (res.ok) {
                message.success("Bloqueo de acceso actualizado correctamente");
                setCazaBloqueoModalVisible(false);
                setSelectedCazaBloqueo(null);
                fetchData();
            } else {
                message.error("Error al actualizar el bloqueo de acceso");
            }
        } catch (err) {
            console.error("Error guardando bloqueo de caza:", err);
            message.error("Error al actualizar el bloqueo de acceso");
        } finally {
            setSavingCazaBloqueo(false);
        }
    };

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 1 } }));

    const fetchData = () => {
        if (userId && id_conjunt) {
            setLoading(true);
            GetConjuntPildoresCaza(id_conjunt, userId).then((data: any[]) => {
                if (data && data.length > 0) {
                    const nomConjunt = data[0]?.nom_conjunt || `Conjunt #${id_conjunt}`;
                    setBreadCrumb([
                        { nom: 'Home', nav: '/home' },
                        { nom: 'Jutge', nav: '/jutge' },
                        { nom: 'Píldores', nav: '/jutge/pildores' },
                        { nom: nomConjunt, nav: `/jutge/pildores/${id_conjunt}` }
                    ], true);
                    const mapped = data.map(item => ({
                        ...item,
                        mustInclude: item.mustInclude
                            ? item.mustInclude.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0)
                            : [],
                        mustNotInclude: item.mustNotInclude
                            ? item.mustNotInclude.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0)
                            : []
                    }));
                    setEscenarios(mapped);
                } else if (data) {
                    // Empty array but valid - still set breadcrumb with id
                    setBreadCrumb([
                        { nom: 'Home', nav: '/home' },
                        { nom: 'Jutge', nav: '/jutge' },
                        { nom: 'Píldores', nav: '/jutge/pildores' },
                        { nom: `Conjunt #${id_conjunt}`, nav: `/jutge/pildores/${id_conjunt}` }
                    ], true);
                }
                setLoading(false);
            });
        }
    };

    // Control de acceso: al construir el componente comprobamos el estado del conjunt
    // (jutge.practica tipus=2). get_practica_id normaliza 'estat': devuelve 3 solo si es
    // VISIBLE y dentro de fechas; cualquier otro valor (borrador, no activa, finalizada,
    // fuera de plazo -> Tancat=0) significa que un alumno NO puede acceder. En ese caso lo
    // mandamos de vuelta al listado de píldores, para que no pueda entrar por URL directa.
    useEffect(() => {
        if (!id_conjunt) return;
        // Admin/profe siempre pueden entrar (para gestionar el conjunt aunque no sea visible).
        if (isAdmin) return;
        let cancelled = false;
        GetPractica(id_conjunt).then((info: any) => {
            if (cancelled) return;
            // Si no hay info o el estado normalizado no es 3 (Actiu/accesible), fuera.
            const estat = info ? Number(info.estat) : null;
            if (!info || estat !== 3) {
                navigate('/jutge/pildores');
            }
        }).catch(() => {
            if (!cancelled) navigate('/jutge/pildores');
        });
        return () => { cancelled = true; };
    }, [id_conjunt, isAdmin, navigate]);

    useEffect(() => { fetchData(); }, [userId, id_conjunt]);

    UseWebSocketSubscription(
        [`/topic/caza/${userId}`],
        async (topic: any, msg: any) => {
            if (msg === true || msg.accion === 'REFRESH') fetchData();
        }
    );

    const onDragEnd = ({ active, over }: any) => {
        if (!over || active.id === over?.id) return;
        setEscenarios(prev => {
            const oldIndex = prev.findIndex(i => i.id === active.id);
            const newIndex = prev.findIndex(i => i.id === over?.id);
            if (oldIndex === -1 || newIndex === -1) return prev;
            const moved = arrayMove(prev, oldIndex, newIndex);
            const ordenList = moved.map((item, idx) => ({ id: item.id, ordre: idx + 1 }));
            fetch(`${process.env.REACT_APP_API_URL}:8061/jutge/caza/ordre`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(ordenList)
            }).catch(() => message.error('Error guardando el orden'));
            return moved;
        });
    };

    const handleEstadoChange = (id: number, nuevoEstado: number) => {
        fetch(`${process.env.REACT_APP_API_URL}:8061/jutge/caza/${id}/estat`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ estat_config: nuevoEstado })
        }).then(res => {
            if (res.ok) { message.success('Estado actualizado'); fetchData(); }
            else message.error('Error al actualizar el estado');
        }).catch(() => message.error('Error de red'));
    };

    const handleClaimAndRefresh = async (record: EscenarioCaza) => {
        if (record.tiempo_limite_superado === 1) {
            message.error(`Has superado el tiempo límite de ${record.reward_time_limit} minutos para conseguir la recompensa.`);
            return;
        }

        let allRecs: any[] = [];
        try {
            message.loading({ content: 'Revisando recompensas...', key: 'claim_caza' });
            allRecs = (await GetCazaRecompenses(record.id, id_conjunt)) || [];
            const groupedRewards = allRecs.filter((r: any) => Number(r.grupo_seleccion) > 0);

            if (groupedRewards.length > 0) {
                message.destroy('claim_caza');
                setSelectionOptions(groupedRewards);
                setPendingSelectionItem(record);
                setSelectableMode(true);
                setRecompensaModalVisible(true);
                return;
            }
        } catch (_) {}

        // Determinar si hay recompensa de tipo Javamon
        // Si hay recompensas configuradas en caza_recompensa, comprobamos si alguna es Javamon
        let isJavamonReward = false;
        if (allRecs.length > 0) {
            isJavamonReward = allRecs.some((r: any) => {
                const t = (r.tipo || '').toUpperCase();
                return t === 'JAVAMON' || t === 'JAVAMON_RANDOM' || t === 'JAVAMON_SEASON';
            });
        } else {
            // Si no hay recompensas en caza_recompensa pero tiene configurado reward_type / rewardType
            const rawType = record.reward_type || record.rewardType;
            if (rawType) {
                const rt = rawType.toUpperCase();
                isJavamonReward = rt === 'JAVAMON' || rt === 'JAVAMON_RANDOM' || rt === 'JAVAMON_SEASON';
            } else {
                // Si ni siquiera hay reward_type y tampoco hay javamon_config_id, NO es Javamon
                isJavamonReward = Boolean(record.javamon_config_id);
            }
        }

        if (isJavamonReward) {
            try {
                message.loading({ content: 'Detectando recompensa...', key: 'claim_caza' });
                const res = await EnsureCazaJavamon(record.id + '', userId, id_conjunt ? parseInt(id_conjunt) : null);
                message.destroy('claim_caza');
                if (res && (res.image || res.nom || res.id)) {
                    let r: 'normal' | 'epic' | 'legendary' = 'normal';
                    if (res.image && res.image.includes('epic')) r = 'epic';
                    else if (res.image && res.image.includes('legendary')) r = 'legendary';
                    const jm = { id: res.id, name: res.nom || 'Javamon', image: getExternalJavamonImage(res), rarity: r };
                    setPendingJavamonBattle({ idCaza: record.id, javamon: jm });
                    setSelectableMode(false);
                    setRecompensaItems([{ tipo: 'JAVAMON', data: jm }]);
                    setRecompensaModalVisible(true);
                } else {
                    message.error({ content: 'No se ha encontrado Javamon asignado.', key: 'claim_caza' });
                }
            } catch (err) { message.error({ content: 'Error al preparar la recompensa', key: 'claim_caza' }); }
        } else {
            try {
                message.loading({ content: 'Reclamando recompensa...', key: 'claim_caza' });
                const res = await ClaimCazaReward(record.id, userId, id_conjunt ? parseInt(id_conjunt) : null);
                if (res && res.status === 'ok') {
                    message.success({ content: res.message || '¡Recompensa obtenida!', key: 'claim_caza' });
                    setSelectableMode(false);
                    setPendingJavamonBattle(null);
                    setRecompensaItems(res.claimed && res.claimed.length > 0 ? res.claimed : (
                        allRecs.length > 0 ? allRecs : [
                            { tipo: 'PUNTUACION', valor: record.reward_valor || 5 }
                        ]
                    ));
                    setRecompensaModalVisible(true);
                    fetchData();
                } else {
                    message.error({ content: (res && res.message) || 'Error al reclamar la recompensa', key: 'claim_caza' });
                }
            } catch (e) { message.error({ content: 'Error de red al reclamar', key: 'claim_caza' }); }
        }
    };

    const handleConfirmSelection = async (chosenReward: any) => {
        if (!pendingSelectionItem) return;
        try {
            message.loading({ content: 'Reclamando recompensa seleccionada...', key: 'claim_caza' });
            const res = await ClaimCazaReward(pendingSelectionItem.id, userId, id_conjunt ? parseInt(id_conjunt) : null, chosenReward.id);
            if (res && res.status === 'ok') {
                message.success({ content: res.message || '¡Recompensa elegida con éxito!', key: 'claim_caza' });
                setSelectableMode(false);
                setRecompensaItems(res.claimed && res.claimed.length > 0 ? res.claimed : [chosenReward]);
                fetchData();
            } else {
                message.error({ content: (res && res.message) || 'Error al reclamar la selección', key: 'claim_caza' });
            }
        } catch (e) {
            message.error({ content: 'Error de red al reclamar selección', key: 'claim_caza' });
        }
    };

    const { confirm } = Modal;
    const showConfirm = (record: EscenarioCaza) => {
        const isUnlimited = record.intentos_global === 0 || record.intentos_global === null || record.intentos_global === undefined;
        if (!isUnlimited && record.intentos_restantes !== undefined && record.intentos_restantes <= 0) {
            Modal.warning({ title: 'Sin intentos', content: 'No te quedan intentos para esta caza.' });
            return;
        }
        const props: ModalFuncProps = {
            title: '🚀 ¡Preparado para el Aprendizaje Guiado?',
            content: (
                <div style={{ lineHeight: '1.6em' }}>
                    <p>📚 <strong>Aprendizaje Paso a Paso:</strong> Irás resolviendo el ejercicio de forma incremental.</p>
                    <p>📖 <strong>Teoría Integrada:</strong> Tendrás acceso directo a la documentación de teoría en cualquier momento.</p>
                    <p>⚡ <strong>Validación por Tramos:</strong> Podrás validar cada paso conservando tu código para el siguiente.</p>
                    <p>⏱ Dispondrás de tiempo para completar la misión y ganar tu recompensa.</p>
                </div>
            ),
            okText: '¡Empezar!',
            cancelText: 'Cancelar',
            onOk() {
                PostCazaStart(record.id.toString(), userId, id_conjunt ? parseInt(id_conjunt) : null).then(() => {
                    setEscenarioActivo(record);
                }).catch(e => {
                    console.error('Error starting Caza:', e);
                    Modal.error({ title: 'Error', content: 'No se pudo iniciar la sesión.' });
                });
            },
        };
        (confirm as (props: ModalFuncProps) => void)(props);
    };

    // Celda de recompensa (solo visible para admins/profes): muestra el tipo de
    // recompensa asignada a la pildora con su icono/imagen y un tooltip descriptivo.
    const renderRewardCell = (record: EscenarioCaza) => {
        const tipo = (record.reward_type || record.rewardType || '').toUpperCase();
        const apiUrl = process.env.REACT_APP_API_URL;

        if (!tipo) {
            return <Tag color="default" style={{ margin: 0 }}>Sense recompensa</Tag>;
        }

        // Javamon especifico: muestra su imagen
        if (tipo === 'JAVAMON' && record.javamon_config_id) {
            const url = `${apiUrl}:8061/jutge/game/image/javamon/${record.javamon_config_id}`;
            return (
                <Tooltip title={`Javamon específic #${record.javamon_config_id}`}>
                    <img src={url} alt="javamon" style={{ width: 26, height: 26, objectFit: 'contain', borderRadius: '50%' }} />
                </Tooltip>
            );
        }
        // Javamon aleatorio / de temporada: no hay imagen concreta (se decide al reclamar)
        if (tipo === 'JAVAMON_RANDOM') {
            return <Tooltip title="Javamon aleatori"><Tag color="purple" style={{ margin: 0 }}>⚔️ Aleatori</Tag></Tooltip>;
        }
        if (tipo === 'JAVAMON_SEASON') {
            return <Tooltip title={`Javamon de temporada${record.itemId ? ` #${record.itemId}` : ''}`}><Tag color="purple" style={{ margin: 0 }}>❓ Temporada</Tag></Tooltip>;
        }
        // Puntuacion
        if (tipo === 'PUNTUACION') {
            return <Tooltip title="Puntuació"><Tag color="gold" style={{ margin: 0, color: '#000' }}>🪙 +{record.reward_valor ?? 0}</Tag></Tooltip>;
        }
        // Items (tienda, skin, arma, etc.): imagen por endpoint
        if (record.itemId) {
            let seg = tipo.toLowerCase();
            if (seg === 'escudo') seg = 'escut';
            else if (seg === 'arco') seg = 'arc';
            else if (seg === 'shop_item') seg = 'shop';
            const url = `${apiUrl}:8061/jutge/game/image/${seg}/${record.itemId}`;
            return (
                <Tooltip title={`${tipo} #${record.itemId}`}>
                    <img src={url} alt={tipo} style={{ width: 26, height: 26, objectFit: 'contain' }} />
                </Tooltip>
            );
        }
        // Fallback: solo el tipo
        return <Tooltip title={tipo}><Tag style={{ margin: 0 }}>🎁 {tipo}</Tag></Tooltip>;
    };

    const rewardColumn = {
        key: 'reward',
        title: 'Recompensa',
        align: 'center' as const,
        width: 140,
        render: (_: any, record: EscenarioCaza) => (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
                <CazaRecompensesTags
                    id_caza={record.id}
                    idConjunt={id_conjunt}
                    ele={record}
                    refreshKey={refreshKey}
                />
            </div>
        ),
    };

    const baseColumns: ColumnsType<EscenarioCaza> = [
        { key: 'id', dataIndex: 'id', width: 20 },
        {
            key: 'state',
            title: 'Estado',
            align: 'center',
            width: 140,
            render: (_, record) => {
                const { estat, obtained, estat_config, totalPasos, max_paso_completado } = record;
                const totalP = totalPasos || 0;
                const maxCompletado = max_paso_completado || 0;
                const allStepsDone = totalP > 0 && maxCompletado >= totalP;

                const getConfigTag = () => {
                    if (estat_config === 2) return <Tag color="#8c8c8c" style={{ fontWeight: 600, margin: 0 }}>Finalitzat</Tag>;
                    if (estat_config === 0) return <Tag color="#fa8c16" style={{ fontWeight: 600, margin: 0 }}>En Edició</Tag>;
                    if (obtained === 1) return <Tag color="#52c41a" style={{ fontWeight: 600, margin: 0 }} icon={<CheckCircleOutlined />}>Realizado</Tag>;
                    if (estat === -1) return <Tag color="#1890ff" style={{ fontWeight: 600, margin: 0 }} icon={<SyncOutlined spin />}>Evaluando...</Tag>;
                    if (allStepsDone && (!obtained || obtained === 0))
                        return <Tag color="#faad14" style={{ fontWeight: 700, margin: 0, color: '#7d4e00' }} icon={<GiftOutlined />}>¡Recompensa!</Tag>;
                    if (totalP > 0 && maxCompletado > 0)
                        return <Tag color="#13c2c2" style={{ fontWeight: 600, margin: 0 }} icon={<SyncOutlined />}>{maxCompletado} de {totalP}</Tag>;
                    if (estat === 0) return <Tag color="#f5222d" style={{ fontWeight: 600, margin: 0 }} icon={<CloseCircleOutlined />}>Fallado</Tag>;
                    return <Tag color="#d9d9d9" style={{ fontWeight: 600, margin: 0, color: '#595959' }}>Pendiente</Tag>;
                };

                if (isAdmin) {
                    return (
                        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                            {getConfigTag()}
                            <Select
                                value={estat_config === 2 ? 2 : (estat_config === 0 ? 0 : 1)}
                                onChange={val => handleEstadoChange(record.id, val)}
                                style={{ width: '110px' }}
                                size="small"
                                options={[
                                    { value: 0, label: <><span style={{ color: '#fa8c16', fontWeight: 700 }}>● </span>En Edició</> },
                                    { value: 1, label: <><span style={{ color: '#52c41a', fontWeight: 700 }}>● </span>Visible</> },
                                    { value: 2, label: <><span style={{ color: '#8c8c8c', fontWeight: 700 }}>● </span>Finalitzat</> },
                                ]}
                            />
                        </div>
                    );
                }
                return getConfigTag();
            }
        },
        {
            key: 'imagen',
            dataIndex: 'imagen',
            width: 60,
            render: (_: any, record: EscenarioCaza) => <CazaImagen id={record.id} />,
        },
        {
            key: 'nombre',
            dataIndex: 'nombre',
            title: 'Píldora',
            render: (text: string, record: EscenarioCaza) => {
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text strong style={{ fontSize: '1rem' }}>{text}</Text>
                        <Text type="secondary" style={{ fontSize: '0.8rem', marginBottom: 2 }}>{record.descripcion}</Text>
                    </div>
                );
            }
        },
        {
            key: 'dificultad',
            dataIndex: 'dificultad',
            title: 'Dificultad',
            align: 'center',
            render: (d: string) => <Tag color={dificultadColor[d]} style={{ fontWeight: 'bold' }}>{d}</Tag>,
        },
        {
            key: 'intentos',
            title: 'Intentos',
            align: 'center',
            render: (_, record) => {
                const isUnlimited = record.intentos_global === 0 || record.intentos_global === null || record.intentos_global === undefined;
                if (isUnlimited) return <Tag color="green">Ilimitados</Tag>;
                const restantes = record.intentos_restantes !== undefined ? record.intentos_restantes : 0;
                return <Tag color={restantes > 0 ? 'blue' : 'red'}>{restantes} / {record.intentos_global}</Tag>;
            }
        },
        {
            key: 'tiempo',
            title: 'Tiempo',
            align: 'center',
            render: (_, record) => {
                const limitMin = record.reward_time_limit;
                const elapsedSec = record.tiempo_empleado_segundos;
                const isExceeded = record.tiempo_limite_superado === 1;
                const formatSec = (sec?: number) => {
                    if (sec === undefined || sec === null) return '--:--';
                    return `${Math.floor(sec / 60)}m ${String(sec % 60).padStart(2, '0')}s`;
                };
                return (
                    <Space direction="vertical" size={2}>
                        {limitMin ? (
                            <Tooltip title={`Tiempo límite para recompensa: ${limitMin} minutos`}>
                                <Tag color={isExceeded ? 'red' : 'orange'} style={{ fontSize: '0.75rem' }}>⏱️ Máx: {limitMin}m</Tag>
                            </Tooltip>
                        ) : null}
                        {elapsedSec !== undefined && elapsedSec !== null ? (
                            <Tag color={isExceeded ? 'volcano' : 'cyan'} style={{ fontSize: '0.75rem' }}>
                                {isExceeded ? '⚠️ ' : '⌛ '}{formatSec(elapsedSec)}
                            </Tag>
                        ) : (!limitMin && <span style={{ color: '#aaa', fontSize: '0.8rem' }}>-</span>)}
                    </Space>
                );
            }
        },
        {
            key: 'acciones',
            title: 'Acciones',
            align: 'center',
            render: (_, record) => {
                const { estat, obtained, estat_config } = record;
                const isVisible = estat_config === 1; // 0=En Edició, 1=Visible, 2=Finalitzat
                const isCaptured = obtained === 1;
                const isEvaluating = estat === -1;
                const isUnlimited = record.intentos_global === 0 || record.intentos_global === null || record.intentos_global === undefined;
                const isOutOfAttempts = !isUnlimited && record.intentos_restantes !== undefined && record.intentos_restantes <= 0;
                const totalP = record.totalPasos || 0;
                const maxCompletado = record.max_paso_completado || 0;
                const allStepsDone = totalP > 0 && maxCompletado >= totalP;
                // Un alumno solo puede entrar si la píldora está Visible (estat_config === 1)
                const canStudentEnter = isVisible && !isCaptured && !isEvaluating && !allStepsDone;
                // Puede revisar sus entregas una vez finalizada (completada / recompensa obtenida)
                const canStudentReview = !isAdmin && (isCaptured || allStepsDone);

                return (
                    <Space>
                        {isAdmin && (
                            <>
                                {!isUnlimited && (
                                    <Tooltip title="Editar Intentos (Admin)">
                                        <Button shape="circle" onClick={e => { e.stopPropagation(); setSelectedCaza(record); setEditAttemptsVisible(true); }}
                                            icon={<BiEdit />} style={{ backgroundColor: '#1890ff', border: 'none', color: '#fff', marginRight: 5 }} />
                                    </Tooltip>
                                )}
                                <Tooltip title="Editar Píldora">
                                    <Button shape="circle" icon={<EditOutlined />}
                                        onClick={e => { e.stopPropagation(); setEditingCaza(record); setNewCazaVisible(true); }}
                                        style={{ marginRight: 5 }} />
                                </Tooltip>
                                <Tooltip title={record.id_bloqueo ? `Bloqueo activo: requiere "${record.bloqueo_nombre || ('Píldora #' + record.id_bloqueo)}"` : "Configurar bloqueo de acceso"}>
                                    <Button
                                        shape="circle"
                                        icon={<LockOutlined />}
                                        onClick={e => handleOpenBloqueoModal(record, e)}
                                        style={{
                                            backgroundColor: record.id_bloqueo ? '#fff7e6' : '#fafafa',
                                            borderColor: record.id_bloqueo ? '#ffa940' : '#d9d9d9',
                                            color: record.id_bloqueo ? '#fa8c16' : '#8c8c8c',
                                            marginRight: 5
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title="Configurar Recompensas">
                                    <Button shape="circle" icon={<TrophyOutlined />}
                                        onClick={e => {
                                            e.stopPropagation();
                                            setSelectedCazaRecompenses(record);
                                            setCazaRecompensesVisible(true);
                                        }}
                                        style={{ backgroundColor: '#fa8c16', border: 'none', color: '#fff', marginRight: 5 }} />
                                </Tooltip>
                                <Tooltip title="Ver Estadísticas Alumnos">
                                    <Button shape="circle" icon={<UserOutlined />}
                                        onClick={e => { e.stopPropagation(); setActiveAdminCaza(record); setAdminModalVisible(true); }}
                                        style={{ backgroundColor: '#722ed1', border: 'none', color: '#fff', marginRight: 5 }} />
                                </Tooltip>
                            </>
                        )}
                        {allStepsDone && (!obtained || obtained === 0) && (
                            record.tiempo_limite_superado === 1 ? (
                                <Tooltip title={`Tiempo límite superado (${record.reward_time_limit} min).`}>
                                    <Button shape="circle" icon={<GiftOutlined />} disabled style={{ backgroundColor: '#f5f5f5', color: '#d9d9d9', borderColor: '#d9d9d9' }} />
                                </Tooltip>
                            ) : (
                                <Tooltip title="Reclamar Recompensa!">
                                    <Button shape="circle" icon={<GiftOutlined />} className="animate-bounce"
                                        style={{ backgroundColor: '#f5222d', color: '#fff', border: 'none', boxShadow: '0 2px 8px rgba(245,34,45,0.4)' }}
                                        onClick={() => handleClaimAndRefresh(record)} />
                                </Tooltip>
                            )
                        )}
                        {/* Revisar las propias entregas (solo alumno y solo tras finalizar la píldora) */}
                        {canStudentReview && (
                            <Tooltip title="Veure les meves entregues">
                                <Button shape="circle" icon={<FaEye />}
                                    onClick={e => { e.stopPropagation(); setReviewCaza(record); }}
                                    style={{ backgroundColor: '#722ed1', border: 'none', color: '#fff' }} />
                            </Tooltip>
                        )}
                        {(() => {
                            if (!isAdmin && !canStudentEnter) return null;

                            const isStudent = !isAdmin;
                            const isGameMode = typeof window !== "undefined" && (window.location.search.includes("fromGame=true") || window.self !== window.top);
                            const isChallengeMode = (user as any)?.modoDesafio === 1 || (user as any)?.modoDesafio === true;
                            const isLocked = (isChallengeMode || isGameMode) && isStudent && record.id_bloqueo && !record.bloqueo_intentado;

                            if (isLocked) {
                                const reqName = record.bloqueo_nombre || "la píldora previa requerida";
                                return (
                                    <Tooltip title={`🔒 Bloqueado: debes realizar al menos un intento en "${reqName}" para desbloquear el acceso.`}>
                                        <Button
                                            disabled
                                            shape="circle"
                                            icon={<LockOutlined />}
                                            style={{ backgroundColor: '#f5f5f5', border: '1px solid #d9d9d9', color: '#bfbfbf', cursor: 'not-allowed' }}
                                        />
                                    </Tooltip>
                                );
                            }

                            return (
                                <Tooltip title={isOutOfAttempts ? 'Sin intentos' : 'Entrar en la píldora'}>
                                    <Button shape="circle" icon={<BiArrowFromLeft />}
                                        disabled={!isAdmin && (estat === -1 || isOutOfAttempts)}
                                        style={{ backgroundColor: (!isAdmin && isOutOfAttempts) ? '#ccc' : '#52c41a', border: 'none', color: '#fff' }}
                                        onClick={() => showConfirm(record)} />
                                </Tooltip>
                            );
                        })()}
                    </Space>
                );
            }
        },
    ];

    // Para admins/profes insertamos la columna de recompensa (antes de "Acciones")
    // y el handle de arrastre al inicio.
    const adminBaseColumns: ColumnsType<EscenarioCaza> = (() => {
        const accionsIdx = baseColumns.findIndex((c: any) => c.key === 'acciones');
        if (accionsIdx === -1) return [...baseColumns, rewardColumn];
        const copy = [...baseColumns];
        copy.splice(accionsIdx, 0, rewardColumn);
        return copy;
    })();

    const columns = isAdmin ? [
        { key: 'sort', align: 'center' as const, width: 30, render: () => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} /> },
        ...adminBaseColumns
    ] : baseColumns;

    return (
        <>
            <Card
                bordered={false}
                style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.08)', borderRadius: 12, background: '#fff', margin: '16px 0' }}
            >
                <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
                    <SortableContext items={escenarios.map(i => i.id.toString())} strategy={verticalListSortingStrategy}>
                        <Table
                            components={isAdmin ? { body: { row: DragRow } } : undefined}
                            columns={columns}
                            dataSource={escenarios}
                            showHeader={false}
                            rowKey={r => r.id.toString()}
                            pagination={{ pageSize: 10 }}
                            size="small"
                            bordered={false}
                            loading={loading}
                            style={{ borderRadius: 12, overflow: 'hidden', background: '#fff' }}
                        />
                    </SortableContext>
                </DndContext>
            </Card>

            {escenarioActivo && (
                <CazaModal
                    escenario={escenarioActivo}
                    isRetro={true}
                    idConjunt={id_conjunt ? parseInt(id_conjunt) : null}
                    onExit={() => { setEscenarioActivo(null); fetchData(); }}
                />
            )}

            {selectedCaza && (
                <EditAttemptsModal
                    visible={editAttemptsVisible}
                    onClose={() => { setEditAttemptsVisible(false); fetchData(); }}
                    idCaza={selectedCaza.id}
                    nomCaza={selectedCaza.nombre}
                />
            )}

            {cazaBloqueoModalVisible && selectedCazaBloqueo && (
                <Modal
                    open={cazaBloqueoModalVisible}
                    title={
                        <Space>
                            <LockOutlined style={{ color: '#fa8c16' }} />
                            <span>Bloqueo de acceso (Prerrequisito) - {selectedCazaBloqueo.nombre}</span>
                        </Space>
                    }
                    onCancel={() => {
                        setCazaBloqueoModalVisible(false);
                        setSelectedCazaBloqueo(null);
                    }}
                    onOk={handleSaveCazaBloqueo}
                    confirmLoading={savingCazaBloqueo}
                    okText="Guardar"
                    cancelText="Cancelar"
                    centered
                >
                    <div style={{ padding: '8px 0' }}>
                        <p style={{ color: '#595959', fontSize: 13, marginBottom: 16 }}>
                            Configura qué píldora previa debe haber sido intentada <strong>al menos una vez</strong> por el alumno antes de poder acceder a esta píldora.
                            <br />
                            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                                (Este bloqueo solo se aplica en <strong>Modo Desafío</strong> y <strong>Modo Juego</strong>; en el modo normal el acceso no se bloquea).
                            </span>
                        </p>

                        <div style={{ marginBottom: 8, fontWeight: 600 }}>Píldora previa requerida:</div>
                        <Select
                            allowClear
                            placeholder="Sin bloqueo (Acceso libre)"
                            style={{ width: '100%' }}
                            value={selectedCazaBloqueoId}
                            onChange={(val) => setSelectedCazaBloqueoId(val)}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option: any) => {
                                const text = Array.isArray(option?.children)
                                    ? option.children.join(' ')
                                    : String(option?.children ?? option?.label ?? '');
                                return text.toLowerCase().includes(input.toLowerCase());
                            }}
                        >
                            {(escenarios || [])
                                .filter((c: any) => c.id !== selectedCazaBloqueo.id)
                                .map((c: any) => (
                                    <Select.Option key={c.id} value={c.id}>
                                        🔒 {c.nombre} {c.aula ? `(${c.aula})` : ''}
                                    </Select.Option>
                                ))
                            }
                        </Select>
                    </div>
                </Modal>
            )}

            <NouCazaModal
                visible={newCazaVisible}
                onClose={() => { setNewCazaVisible(false); setEditingCaza(null); }}
                onSuccess={() => { setNewCazaVisible(false); setEditingCaza(null); fetchData(); }}
                caza={editingCaza}
                defaultConjunt={id_conjunt ? parseInt(id_conjunt) : undefined}
                hideAula
                availableCazas={escenarios}
            />

            {selectedCazaRecompenses && (
                <PildoraRecompensesModal
                    visible={cazaRecompensesVisible}
                    onClose={(hasChanges) => {
                        setCazaRecompensesVisible(false);
                        setSelectedCazaRecompenses(null);
                        if (hasChanges) {
                            setRefreshKey(k => k + 1);
                            fetchData();
                        }
                    }}
                    caza={selectedCazaRecompenses}
                    idConjunt={id_conjunt}
                />
            )}

            {recompensaModalVisible && (
                <RecompensaSummaryModal
                    visible={recompensaModalVisible}
                    onClose={() => {
                        setRecompensaModalVisible(false);
                        setSelectableMode(false);
                        setSelectionOptions([]);
                        setPendingSelectionItem(null);
                        if (pendingJavamonBattle) {
                            setActiveBattleCazaId(pendingJavamonBattle.idCaza);
                            setBattleJavamon(pendingJavamonBattle.javamon);
                            setShowBattle(true);
                            setPendingJavamonBattle(null);
                        }
                    }}
                    rewards={recompensaItems}
                    selectableMode={selectableMode}
                    selectionOptions={selectionOptions}
                    onConfirmSelection={handleConfirmSelection}
                />
            )}

            {showBattle && (
                <JavaMon
                    userId={userId}
                    onClose={() => { setShowBattle(false); fetchData(); }}
                    onSuccess={() => {
                        if (activeBattleCazaId) {
                            ClaimCazaReward(activeBattleCazaId, userId, id_conjunt ? parseInt(id_conjunt) : null).then(() => {
                                message.success('¡Javamon capturado y registrado con éxito!');
                                fetchData();
                            });
                        }
                    }}
                    forcedJavamon={battleJavamon}
                />
            )}

            {activeAdminCaza && (
                <CazaAdminModal
                    visible={adminModalVisible}
                    onClose={() => setAdminModalVisible(false)}
                    idCaza={activeAdminCaza.id.toString()}
                    cazaTitle={activeAdminCaza.nombre}
                    caza={activeAdminCaza}
                    idConjunt={id_conjunt ? parseInt(id_conjunt) : null}
                />
            )}

            {/* Revisión de las propias entregas del alumno (solo tras finalizar) */}
            {reviewCaza && (
                <PildoraAlumneDetailModal
                    visible={!!reviewCaza}
                    onClose={() => setReviewCaza(null)}
                    idCaza={String(reviewCaza.id)}
                    idUsuari={userId}
                    pildoraTitle={reviewCaza.nombre}
                    studentName={'Les meves entregues'}
                    totalSteps={reviewCaza.totalPasos || 1}
                    instrucciones={reviewCaza.instrucciones}
                    idConjunt={id_conjunt ? parseInt(id_conjunt) : null}
                    canDelete={false}
                />
            )}

            <ImportarCazaExcelModal
                visible={importExcelVisible}
                onClose={() => setImportExcelVisible(false)}
                onSuccess={fetchData}
                defaultConjunt={id_conjunt ? parseInt(id_conjunt) : undefined}
            />

            <FloatButton.Group shape="circle" style={{ left: 24, bottom: 24 }}>
                <FloatButton icon={<SyncOutlined />} onClick={fetchData} tooltip="Refrescar" />
                {isAdmin && (
                    <>
                        <FloatButton
                            icon={<FileExcelOutlined style={{ color: '#217346' }} />}
                            onClick={() => setImportExcelVisible(true)}
                            tooltip="Crear Píldora desde Excel"
                        />
                        <FloatButton
                            icon={<PlusOutlined />}
                            onClick={() => { setEditingCaza(null); setNewCazaVisible(true); }}
                            tooltip="Nueva Píldora"
                        />
                    </>
                )}
            </FloatButton.Group>
        </>
    );
};

export default PildoresIdPildores;
